// Iter54 — Self-service freemium signup page at /signup.
//
// Two-step flow:
//   1. Collect email + company + role + password → POST /api/auth/signup
//   2. UI shows "check your email" + (dev) auto-verify link. User clicks
//      verify → routed to /verify?token=… → VerifySignup page calls
//      /api/auth/verify-signup and drops them into the app authenticated.
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "#f0f2f8", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, {
        email: email.trim(),
        company_name: company.trim(),
        role: role.trim(),
        password,
        name: name.trim() || null,
      });
      setDone(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 30, color: "#f0f2f8", letterSpacing: "-0.5px" }}>
          Check your email
        </h1>
        <p className="mt-3" style={mono(12, "#c5cad8")}>
          We sent a verification link to <strong>{email}</strong>. Click it to
          activate your freemium tenant and start using SITROOM. The link
          expires in 48 hours.
        </p>
        {done.dev_verify_url && (
          <div
            className="mt-5 px-3 py-2 rounded"
            style={{ background: "rgba(243,177,74,0.10)", border: "1px dashed rgba(243,177,74,0.40)", ...mono(10, "#f3b14a") }}
            data-testid="signup-dev-verify"
          >
            Dev mode — email is mocked. <Link to={done.dev_verify_url}
              style={{ color: "#f3b14a", textDecoration: "underline" }}
              data-testid="signup-dev-verify-link">Verify now</Link>
          </div>
        )}
        <div className="mt-6">
          <Link to="/login" style={mono(11, "#9ec3ec", 700, "0.4px")} data-testid="signup-back-login">← Back to login</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 30, color: "#f0f2f8", letterSpacing: "-0.5px" }} data-testid="signup-title">
        Start your free SITROOM
      </h1>
      <p className="mt-2" style={mono(11, "#9aa1b3")}>
        Create your isolated freemium tenant — 5 FMEAs · 3 8D reports · 10 incidents/month.
        Upgrade anytime.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
        <Field label="Work email" testId="signup-email">
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourcompany.com" autoComplete="email"
            style={inputStyle} data-testid="signup-email-input"
          />
        </Field>
        <Field label="Your full name (optional)" testId="signup-name">
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe" autoComplete="name"
            style={inputStyle} data-testid="signup-name-input"
          />
        </Field>
        <Field label="Company name" testId="signup-company">
          <input
            type="text" required value={company} onChange={(e) => setCompany(e.target.value)}
            placeholder="Acme Automotive GmbH" autoComplete="organization"
            style={inputStyle} data-testid="signup-company-input"
          />
        </Field>
        <Field label="Your role" testId="signup-role">
          <input
            type="text" required value={role} onChange={(e) => setRole(e.target.value)}
            placeholder="Quality Manager, Plant Manager, …"
            style={inputStyle} data-testid="signup-role-input"
          />
        </Field>
        <Field label="Password (min 8 chars)" testId="signup-password">
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password" minLength={8}
            style={inputStyle} data-testid="signup-password-input"
          />
        </Field>

        {error && (
          <div
            className="px-3 py-2 rounded"
            style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", ...mono(11, "#ff9a99") }}
            data-testid="signup-error"
          >{error}</div>
        )}

        <button
          type="submit" disabled={busy}
          className="w-full px-4 py-2.5 rounded-md"
          style={{
            background: "#185FA5", color: "#fff",
            fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14,
            letterSpacing: "0.4px", border: "none",
            cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
          }}
          data-testid="signup-submit"
        >{busy ? "Creating your tenant…" : "Sign up · Freemium"}</button>
      </form>

      <div className="mt-6 text-center">
        <Link to="/login" style={mono(11, "#9aa1b3", 600, "0.3px")} data-testid="signup-have-account">
          Already have an account? <span style={{ color: "#9ec3ec" }}>Sign in</span>
        </Link>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div
    style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #08090d 0%, #111420 100%)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}
    data-testid="signup-page"
  >
    <div
      style={{
        width: "100%", maxWidth: 440,
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 8, padding: 32,
      }}
    >
      <div className="mb-2" style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: "0.4px", color: "#9ec3ec" }}>
        SITROOM
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="mb-1 uppercase" style={mono(9, "#9aa1b3", 700, "0.8px")}>{label}</div>
    {children}
  </div>
);

const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  borderRadius: 6,
  background: "var(--bg-base, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize: 12,
  outline: "none",
};
